import clsx from "clsx";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronDown, faChevronRight, faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  type RuleCheckResult,
  type ScreeningDocument,
} from "@/services/api/guardiansv2/types";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";
import { EntityRichText } from "../shared/EntityRichText";
import { entityTextScopeFromAuditScope, type EntityTextScope } from "../shared/entityTags";

function trimmed(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function isRecovered(observed?: RuleCheckResult["observed"]): boolean {
  if (!observed || typeof observed !== "object") return false;
  return observed.recovered === true;
}

const STATUS_STYLES: Record<string, string> = {
  passed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  failed: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  error: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  ignored: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

function ComparisonIcon({ check }: { check: RuleCheckResult }) {
  const passed = check.status === "passed";
  const ignored = check.status === "ignored";

  return (
    <span
      className={clsx(
        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
        ignored
          ? "bg-slate-500/20 text-slate-300"
          : passed
            ? "bg-emerald-500/20 text-emerald-300"
            : "bg-rose-500/20 text-rose-300"
      )}
      aria-hidden
    >
      {ignored ? (
        <span className="text-[10px] font-bold">—</span>
      ) : (
        <FontAwesomeIcon icon={passed ? faCheck : faXmark} className="h-2.5 w-2.5" />
      )}
    </span>
  );
}

function ScreeningTextSection({
  title,
  testId,
  text,
  entityScope,
  scopeLabels,
}: {
  title: string;
  testId?: string;
  text: string;
  entityScope: EntityTextScope;
  scopeLabels?: ScopeLabelCache;
}) {
  return (
    <section data-testid={testId}>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
      <div className="text-sm leading-relaxed text-slate-300">
        <EntityRichText text={text} scope={entityScope} scopeLabels={scopeLabels} />
      </div>
    </section>
  );
}

function RichSummary({
  text,
  entityScope,
  scopeLabels,
}: {
  text: string;
  entityScope: EntityTextScope;
  scopeLabels?: ScopeLabelCache;
}) {
  return (
    <EntityRichText
      text={text}
      scope={entityScope}
      scopeLabels={scopeLabels}
      className="text-sm leading-relaxed text-slate-100"
    />
  );
}

function ObservedBlock({
  observed,
  evidence,
}: {
  observed?: RuleCheckResult["observed"];
  evidence?: string[];
}) {
  const [open, setOpen] = useState(false);
  const hasObserved = observed !== undefined && observed !== null;
  const evidenceItems = evidence ?? [];
  const hasEvidence = evidenceItems.length > 0;

  if (!hasObserved && !hasEvidence) return null;

  const observedText =
    hasObserved && typeof observed === "string"
      ? observed
      : hasObserved
        ? JSON.stringify(observed, null, 2)
        : null;

  return (
    <details className="mt-1.5" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-wide text-slate-500">
        Observed value
      </summary>
      <div className="mt-1 space-y-1.5">
        {hasEvidence ? (
          <ul className="space-y-1">
            {evidenceItems.map((item) => (
              <li
                key={item}
                className="overflow-x-auto rounded bg-slate-900/50 px-2 py-1 font-mono text-[11px] text-slate-300"
              >
                {item}
              </li>
            ))}
          </ul>
        ) : null}
        {observedText ? (
          <pre className="overflow-x-auto rounded bg-slate-900/50 p-2 font-mono text-[11px] text-slate-400">
            {observedText}
          </pre>
        ) : null}
      </div>
    </details>
  );
}

function CheckRow({
  check,
  showSummary = true,
  catalogueKey,
  onEditRule,
  onOpenCheck,
  entityScope,
  scopeLabels,
}: {
  check: RuleCheckResult;
  showSummary?: boolean;
  catalogueKey?: string | null;
  onEditRule?: (catalogueKey: string, ruleId: string) => void;
  onOpenCheck?: (check: RuleCheckResult) => void;
  entityScope: EntityTextScope;
  scopeLabels?: ScopeLabelCache;
}) {
  const canEditRule =
    Boolean(
      catalogueKey &&
        onEditRule &&
        check.rule_id &&
        check.kind !== "signal" &&
        (check.status === "failed" || check.status === "error")
    );
  const title = trimmed(check.title) ?? trimmed(check.rule_id) ?? "Check";
  const summary = showSummary ? trimmed(check.summary) : null;
  const openFromSummary = Boolean(onOpenCheck && check.rule_id && summary);
  const recovered = isRecovered(check.observed);

  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-white/5 bg-slate-800/40 px-3 py-2"
      data-testid={`screening-check-${check.rule_id}`}
    >
      <ComparisonIcon check={check} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 text-sm font-medium text-slate-200">{title}</p>
          {canEditRule ? (
            <button
              type="button"
              onClick={() => onEditRule!(catalogueKey!, check.rule_id)}
              className="shrink-0 text-xs text-slate-500 hover:text-slate-400"
              data-testid={`edit-rule-${check.rule_id}`}
            >
              Edit rule →
            </button>
          ) : null}
        </div>
        {summary ? (
          openFromSummary ? (
            <button
              type="button"
              onClick={() => onOpenCheck!(check)}
              className="mt-0.5 block w-full text-left text-sm leading-relaxed text-slate-100 hover:underline"
              data-testid={`screening-check-open-${check.rule_id}`}
            >
              <RichSummary text={summary} entityScope={entityScope} scopeLabels={scopeLabels} />
            </button>
          ) : (
            <div className="mt-0.5">
              <RichSummary text={summary} entityScope={entityScope} scopeLabels={scopeLabels} />
            </div>
          )
        ) : null}
        {recovered ? (
          <p className="mt-1 text-xs text-emerald-300/90">Recovered — a later attempt succeeded or the task completed.</p>
        ) : null}
        <ObservedBlock observed={check.observed} evidence={check.evidence} />
        {check.error ? (
          <p className="mt-0.5 whitespace-pre-wrap text-xs text-amber-300">{check.error}</p>
        ) : null}
      </div>
      <span
        className={clsx(
          "mt-0.5 shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase",
          STATUS_STYLES[check.status] ?? STATUS_STYLES.error
        )}
      >
        {check.status}
      </span>
    </div>
  );
}

function CheckList({
  checks,
  showSummary = true,
  catalogueKey,
  onEditRule,
  onOpenCheck,
  entityScope,
  scopeLabels,
}: {
  checks: RuleCheckResult[];
  showSummary?: boolean;
  catalogueKey?: string | null;
  onEditRule?: (catalogueKey: string, ruleId: string) => void;
  onOpenCheck?: (check: RuleCheckResult) => void;
  entityScope: EntityTextScope;
  scopeLabels?: ScopeLabelCache;
}) {
  if (checks.length === 0) return null;

  return (
    <div className="space-y-2">
      {checks.map((check) => (
        <CheckRow
          key={`${check.kind}-${check.rule_id}`}
          check={check}
          showSummary={showSummary}
          catalogueKey={catalogueKey}
          onEditRule={onEditRule}
          onOpenCheck={onOpenCheck}
          entityScope={entityScope}
          scopeLabels={scopeLabels}
        />
      ))}
    </div>
  );
}

function CollapsibleCheckGroup({
  title,
  checks,
  defaultOpen = false,
  showSummary = true,
  catalogueKey,
  onEditRule,
  onOpenCheck,
  entityScope,
  scopeLabels,
}: {
  title: string;
  checks: RuleCheckResult[];
  defaultOpen?: boolean;
  showSummary?: boolean;
  catalogueKey?: string | null;
  onEditRule?: (catalogueKey: string, ruleId: string) => void;
  onOpenCheck?: (check: RuleCheckResult) => void;
  entityScope: EntityTextScope;
  scopeLabels?: ScopeLabelCache;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (checks.length === 0) return null;

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mb-2 flex w-full items-center gap-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400"
      >
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="text-[10px]" />
        <span>
          {title} ({checks.length})
        </span>
      </button>
      {open ? (
        <CheckList
          checks={checks}
          showSummary={showSummary}
          catalogueKey={catalogueKey}
          onEditRule={onEditRule}
          onOpenCheck={onOpenCheck}
          entityScope={entityScope}
          scopeLabels={scopeLabels}
        />
      ) : null}
    </section>
  );
}

export interface AlertScreeningThreadProps {
  screening: ScreeningDocument | null;
  loading?: boolean;
  scopeLabels?: ScopeLabelCache;
  compact?: boolean;
  showContextHeader?: boolean;
  /** Long-form alert/screening description — prefer alert.description, fall back to screening outcome. */
  description?: string | null;
  nextStep?: string | null;
  onEditRule?: (catalogueKey: string, ruleId: string) => void;
  onOpenCheck?: (check: RuleCheckResult) => void;
}

/** Task-thread-style trail of alert checks run during screening. */
export function AlertScreeningThread({
  screening,
  loading,
  scopeLabels,
  compact = false,
  showContextHeader = true,
  description,
  nextStep,
  onEditRule,
  onOpenCheck,
}: AlertScreeningThreadProps) {
  if (loading) {
    return <p className="text-sm text-slate-400">Loading check trail…</p>;
  }
  if (!screening) {
    return (
      <p className="text-sm text-slate-500">
        No structured check trail for this alert yet.
      </p>
    );
  }

  const { checks } = screening.checks;
  const failed = checks.filter((c) => c.status === "failed" || c.status === "error");
  const passed = checks.filter((c) => c.status === "passed");
  const ignored = checks.filter((c) => c.status === "ignored");
  const descriptionText = trimmed(description) ?? trimmed(screening.outcome.description);
  const nextStepText = trimmed(nextStep) ?? trimmed(screening.outcome.triage?.next_step);
  const entityScope = entityTextScopeFromAuditScope(screening.scope ?? {});

  const subjectLabel =
    screening.subject?.type === "mission"
      ? (scopeLabels?.missionLabel(screening.scope?.mission_id) ?? "mission")
      : screening.subject?.type === "task"
        ? (scopeLabels?.taskLabel(screening.scope?.task_id ?? screening.subject.id) ?? "task")
        : (screening.subject?.type ?? "screening");

  const contextHeader = screening.catalogue_key
    ? `${screening.catalogue_key} · ${subjectLabel}`
    : subjectLabel;

  return (
    <div className="space-y-4" data-testid="alert-screening-thread">
      {!compact && showContextHeader ? (
        <p className="text-xs text-slate-500" data-testid="screening-context-header">
          {contextHeader}
        </p>
      ) : null}

      {checks.length === 0 ? (
        <p className="text-sm text-slate-500">No checks were evaluated.</p>
      ) : (
        <div className="space-y-4">
          {failed.length > 0 ? (
            <CheckList
              checks={failed}
              showSummary
              catalogueKey={screening.catalogue_key}
              onEditRule={onEditRule}
              onOpenCheck={onOpenCheck}
              entityScope={entityScope}
              scopeLabels={scopeLabels}
            />
          ) : null}
          <CollapsibleCheckGroup
            title="Ignore"
            checks={ignored}
            catalogueKey={screening.catalogue_key}
            onOpenCheck={onOpenCheck}
            entityScope={entityScope}
            scopeLabels={scopeLabels}
          />
          <CollapsibleCheckGroup
            title="Passed"
            checks={passed}
            catalogueKey={screening.catalogue_key}
            onOpenCheck={onOpenCheck}
            entityScope={entityScope}
            scopeLabels={scopeLabels}
          />
        </div>
      )}

      {descriptionText ? (
        <ScreeningTextSection
          title="Description"
          testId="screening-description"
          text={descriptionText}
          entityScope={entityScope}
          scopeLabels={scopeLabels}
        />
      ) : null}

      {nextStepText ? (
        <ScreeningTextSection
          title="Next step"
          testId="screening-next-step"
          text={nextStepText}
          entityScope={entityScope}
          scopeLabels={scopeLabels}
        />
      ) : null}
    </div>
  );
}
